/**
 * Scatter instancing (E3.3.S3, AD-158 constraint 2;
 * docs/architecture/rendering-performance.md §2).
 *
 * AD-158 §2 prohibits a per-instance `THREE.Mesh` for scatter at AP6 density. The
 * builder's two scatter paths — the rock upgrade and the biome scatter dressing —
 * used to clone the template once per slot, so a group of N placements cost
 * N × (leaf meshes) draw calls. This turns a template + N placement transforms
 * into ONE `InstancedMesh` per template leaf mesh: the draw-call count is the
 * template's leaf count, independent of N.
 *
 * All-or-nothing: a template with any behavioural node or a leaf the GPU cannot
 * instance (skinned, morph, multi-material, mirrored) returns null, and the caller
 * keeps its clone path for that group — a template that must stay live is not
 * scatter-shaped, and the static merge (E3.3.S2) still handles plain clones.
 *
 * Render-only, like the merge: colliders come from the shared WR.2 source and
 * never read a render mesh (AD-026/AD-047).
 */

import * as THREE from 'three';

import { behaviourReason } from './staticMerge.js';

/** Name of the group `instanceTemplate` returns (callers skip the static merge for it). */
export const SCATTER_INSTANCES_NAME = 'scatter-instances';

/** One placement's transform, in the scatter group's parent space. */
export interface InstancePlacement {
  x: number;
  y: number;
  z: number;
  rotationY: number;
  scaleX: number;
  scaleY: number;
  scaleZ: number;
}

/** Why a template cannot be instanced, or null when it can. */
export function instanceRefusal(template: THREE.Object3D): string | null {
  let refusal: string | null = null;
  let leaves = 0;
  template.traverse((node) => {
    if (refusal !== null) return;
    const reason = behaviourReason(node);
    if (reason !== null) {
      refusal = reason;
      return;
    }
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    leaves += 1;
    if (Array.isArray(mesh.material)) refusal = 'multi-material';
    else if (Object.keys(mesh.geometry.morphAttributes).length > 0) refusal = 'morph';
  });
  if (refusal === null && leaves === 0) refusal = 'no-mesh';
  return refusal;
}

/**
 * Build one `InstancedMesh` per leaf mesh of `template`, one instance per
 * placement, parented under a returned group. Null when the template is refused
 * (see `instanceRefusal`) or there are no placements.
 */
export function instanceTemplate(
  template: THREE.Object3D,
  placements: readonly InstancePlacement[],
): THREE.Group | null {
  if (placements.length === 0 || instanceRefusal(template) !== null) return null;
  template.updateMatrixWorld(true);
  const rootInverse = template.matrixWorld.clone().invert();
  const group = new THREE.Group();
  group.name = SCATTER_INSTANCES_NAME;
  const placementMatrices = placements.map((p) =>
    new THREE.Matrix4().compose(
      new THREE.Vector3(p.x, p.y, p.z),
      new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), p.rotationY),
      new THREE.Vector3(p.scaleX, p.scaleY, p.scaleZ),
    ),
  );
  const instance = new THREE.Matrix4();
  template.traverse((node) => {
    const mesh = node as THREE.Mesh;
    if (!mesh.isMesh) return;
    // The leaf's transform relative to the template root, so a nested glTF
    // hierarchy lands exactly where `template.clone()` would have put it.
    const local = rootInverse.clone().multiply(mesh.matrixWorld);
    const instanced = new THREE.InstancedMesh(
      mesh.geometry,
      mesh.material as THREE.Material,
      placementMatrices.length,
    );
    instanced.name = mesh.name;
    instanced.castShadow = mesh.castShadow;
    instanced.receiveShadow = mesh.receiveShadow;
    placementMatrices.forEach((placement, i) => {
      instanced.setMatrixAt(i, instance.multiplyMatrices(placement, local));
    });
    instanced.instanceMatrix.needsUpdate = true;
    instanced.computeBoundingSphere();
    group.add(instanced);
  });
  return group;
}
